import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { conditionalValidator, passwordLengthValidator, passwordsMatchValidator } from 'src/app/shared/validators';
import { KeyValuePair } from '../../../shared/models/key-value-pair';
import { FamilyMemberService } from '../../../shared/services/family-member/family-member.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { LoginModel } from '../../auth-models';
import { AuthService, Roles } from '../../auth.service';
import { FamilyMemberPortalRegistrationDto, InvitationVerifyDto } from '../models/family-member-portal-registration-dto';

@Component({
  selector: 'app-family-member-portal-registration',
  templateUrl: './family-member-portal-registration.component.html',
  styleUrls: ['./family-member-portal-registration.component.scss'],
  standalone: false,
})
export class FamilyMemberPortalRegistrationComponent implements OnInit {
  familyMemberPortalRegistration: FamilyMemberPortalRegistrationDto;
  isBusy = false;
  formGroup: UntypedFormGroup;
  accessPreferences: KeyValuePair[] = [
    new KeyValuePair(Roles.FamilyMemberEdit, 'I opt to register for interactive access'),
    new KeyValuePair(Roles.FamilyMemberReadOnly, 'I opt to register for view-only access'),
  ];
  invitationVerifyDto: InvitationVerifyDto;
  portalAccessRetryCount = 0;
  inputType: 'text' | 'password' = 'password';
  inputTypePassCopy: 'text' | 'password' = 'password';
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private familyMemberService: FamilyMemberService,
    private notificationService: NotificationService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    const invitationId = this.route.snapshot.paramMap.get('invitationId');

    this.familyMemberPortalRegistration = {
      invitationId: invitationId,
    } as FamilyMemberPortalRegistrationDto;

    this.familyMemberService.validateInvitation(invitationId).subscribe(
      (response) => {
        if (response.succeeded) {
          this.invitationVerifyDto = response.value;
          this.portalAccessRetryCount = response.value.portalAccessRetryCount;

          // Redirect user to login page if the invitation has already been accepted
          if (response.value.isConfirmed) {
            this.redirectToLogin();
            return;
          } else if (this.portalAccessRetryCount > 2) {
            this.redirectToLocked();
          }

          this.initForm();

          if (this.invitationVerifyDto.hasPassword) {
            this.formGroup.controls.confirmedPassword.clearValidators();
            this.formGroup.controls.familyMemberFirstName.clearValidators();
            this.formGroup.controls.familyMemberLastName.clearValidators();

            this.formGroup.controls.confirmedPassword.updateValueAndValidity();
            this.formGroup.controls.familyMemberFirstName.updateValueAndValidity();
            this.formGroup.controls.familyMemberLastName.updateValueAndValidity();
          }

          if (this.invitationVerifyDto.hasPortalAccount) {
            this.formGroup.controls.accessPreference.clearValidators();
            this.formGroup.controls.consentAccepted.clearValidators();

            this.formGroup.controls.accessPreference.updateValueAndValidity();
            this.formGroup.controls.consentAccepted.updateValueAndValidity();
          }

          this.familyMemberPortalRegistration.familyUserId = response.value.familyUserId;
          this.familyMemberPortalRegistration.familyMemberEmail = response.value.email;

          this.formGroup.patchValue(this.familyMemberPortalRegistration);
          this.formGroup.controls.hasPassword.setValue(this.invitationVerifyDto.hasPassword);
        } else {
          this.notificationService.error(response.errors?.map((x) => x.description).join(','));
        }
      },
      () => {
        this.notificationService.error('Unable to verify invitation.');
      }
    );
  }

  initForm() {
    this.formGroup = new UntypedFormGroup({
      invitationId: new UntypedFormControl('', { validators: [Validators.required] }),
      familyUserId: new UntypedFormControl(''),
      learnerFirstName: new UntypedFormControl('', { validators: [Validators.required] }),
      learnerLastName: new UntypedFormControl('', { validators: [Validators.required] }),
      learnerStateAssignedId: new UntypedFormControl('', {
        validators: [Validators.required, Validators.maxLength(4), Validators.minLength(4)],
      }),
      learnerDateOfBirth: new UntypedFormControl('', { validators: [Validators.required] }),
      password: new UntypedFormControl('', {
        validators: [Validators.required, conditionalValidator(() => !this.formGroup?.get('hasPassword')?.value, passwordLengthValidator)],
      }),
      confirmedPassword: new UntypedFormControl('', { validators: [Validators.required] }),
      familyMemberFirstName: new UntypedFormControl('', { validators: [Validators.required] }),
      familyMemberLastName: new UntypedFormControl('', { validators: [Validators.required] }),
      familyMemberEmail: new UntypedFormControl('', { validators: [Validators.required, Validators.email] }),
      accessPreference: new UntypedFormControl('', { validators: [Validators.required] }),
      consentAccepted: new UntypedFormControl(null, { validators: [Validators.requiredTrue] }),
      hasPassword: new UntypedFormControl(false),
    });

    if (!this.invitationVerifyDto.hasPassword) {
      this.formGroup.setValidators([passwordsMatchValidator]);
    }
  }

  onRegister() {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      this.notificationService.error('Please complete all required fields.');
      return;
    }

    const registrationDto = this.formGroup.value as FamilyMemberPortalRegistrationDto;

    if (registrationDto.consentAccepted !== true && registrationDto.familyUserId === null) {
      this.notificationService.error('You must accept the terms & conditions.');
      return;
    }

    if (registrationDto.familyUserId === null && registrationDto.password !== registrationDto.confirmedPassword) {
      this.notificationService.error('Passwords do not match');
      return;
    }

    this.isBusy = true;

    this.familyMemberService.confirmInvitation(registrationDto).subscribe(
      (response) => {
        if (response.succeeded) {
          this.notificationService.success('Confirmation succeeded');
          // const loginModel = {
          //   password: registrationDto.password,
          //   email: response.value.email,
          // } as LoginModel;
          //this.login(loginModel);
          this.redirectToLogin(); //getting 401 on api calls after trying to login automatically
        } else {
          this.portalAccessRetryCount++;
          if (this.portalAccessRetryCount > 2) {
            this.redirectToLocked();
          } else {
            this.isBusy = false;
            this.notificationService.error(response.errors?.map((x) => x.description).join(','), 4000);
          }
        }
      },
      () => {
        this.isBusy = false;
        this.notificationService.error('Unable to confirm invitation.', 4000);
      }
    );
  }

  login(loginModel: LoginModel) {
    this.authService.login(loginModel).subscribe(
      () => {
        if (this.authService.isAuthenticated) {
          this.authService.navigateToPortal();
        } else {
          this.loginFailed();
        }

        this.isBusy = false;
      },
      () => {
        this.isBusy = false;
        this.loginFailed();
      }
    );
  }

  loginFailed() {
    this.notificationService.error('Login failed, redirecting you to login page...');
    setTimeout(() => {
      this.redirectToLogin();
    }, 3000);
  }

  redirectToLogin() {
    this.router.navigate(['/auth/family/login']);
  }

  redirectToLocked() {
    this.router.navigate(['/auth/family/locked']);
  }

  onRevealOrHide(isVisible: boolean) {
    if (isVisible) this.inputType = 'text';
    else this.inputType = 'password';
  }

  onRevealOrHideCopy(isVisible: boolean) {
    if (isVisible) this.inputTypePassCopy = 'text';
    else this.inputTypePassCopy = 'password';
  }
}
